import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { type Setting } from '@/types';
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, Save, X } from 'lucide-react';
import { useEffect, useState } from 'react';

interface StatusItem {
    value: string;
    label: string;
    color?: string;
    icon?: string;
    isTerminal?: boolean;
    /**
     * Lifecycle stage this status maps to. Only used by settings whose workflow code
     * branches on stage rather than on the status value (calculation requests), so
     * that renaming or adding a status cannot break the workflow.
     */
    stage?: string;
}

interface StatusEditorProps {
    setting: Setting;
    onUpdate: (key: string, value: StatusItem[]) => void;
    /**
     * When supplied, every status must be classified into one of these stages and a
     * stage picker appears in the add/edit forms. Omitted for settings that have no
     * stage concept, which is all of the older ones.
     */
    stages?: readonly string[];
    stageLabel?: (stage: string) => string;
}

export default function StatusEditor({ setting, onUpdate, stages, stageLabel }: StatusEditorProps) {
    const requiresStage = (stages?.length ?? 0) > 0;
    const labelForStage = (stage: string): string => stageLabel?.(stage) ?? stage;
    const [items, setItems] = useState<StatusItem[]>((setting.value as StatusItem[] | string[] | unknown as StatusItem[]) || []);

    // The setting can now also be mutated outside this component — the
    // translations dialog can remove items — so local state has to follow the
    // prop. Without this, a removed item stays on screen and the next edit or
    // reorder submits the stale array and silently resurrects it.
    //
    // Keyed on the serialized value so our own writes (which round-trip to the
    // same content) don't clobber state mid-interaction.
    const serializedValue = JSON.stringify(setting.value ?? []);
    useEffect(() => {
        setItems((current) => {
            const next = (setting.value as StatusItem[] | string[] | unknown as StatusItem[]) || [];
            return JSON.stringify(current) === serializedValue ? current : next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serializedValue]);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [newItem, setNewItem] = useState<StatusItem>({
        value: '',
        label: '',
        color: 'bg-cat-gray text-cat-gray-foreground border-cat-gray-foreground/30',
        icon: '',
        isTerminal: false,
        stage: stages?.[0],
    });

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setItems((items) => {
                const oldIndex = items.findIndex((_, index) => index.toString() === active.id);
                const newIndex = items.findIndex((_, index) => index.toString() === over.id);
                const reorderedItems = arrayMove(items, oldIndex, newIndex);
                onUpdate(setting.key, reorderedItems);
                return reorderedItems;
            });
        }
    };

    const handleAddItem = () => {
        if (newItem.value.trim() && newItem.label.trim()) {
            const updatedItems = [...items, { ...newItem }];
            setItems(updatedItems);
            onUpdate(setting.key, updatedItems);
            setNewItem({
                value: '',
                label: '',
                color: 'bg-cat-gray text-cat-gray-foreground border-cat-gray-foreground/30',
                icon: '',
                isTerminal: false,
                stage: stages?.[0],
            });
            setCreateModalOpen(false);
        }
    };

    const handleUpdateItem = (index: number, updatedItem: StatusItem) => {
        const updatedItems = items.map((item, i) => (i === index ? updatedItem : item));
        setItems(updatedItems);
        onUpdate(setting.key, updatedItems);
        setEditingIndex(null);
    };

    const handleRemoveItem = (index: number) => {
        const updatedItems = items.filter((_, i) => i !== index);
        setItems(updatedItems);
        onUpdate(setting.key, updatedItems);
    };

    const handleEditItem = (index: number) => {
        setEditingIndex(index);
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
    };

    const predefinedColors = [
        'bg-cat-gray text-cat-gray-foreground border-cat-gray-foreground/30',
        'bg-info-subtle text-info-subtle-foreground border-info-subtle-foreground/30',
        'bg-success-subtle text-success-subtle-foreground border-success-subtle-foreground/30',
        'bg-warning-subtle text-warning-subtle-foreground border-warning-subtle-foreground/30',
        'bg-danger-subtle text-danger-subtle-foreground border-danger-subtle-foreground/30',
        'bg-cat-purple text-cat-purple-foreground border-cat-purple-foreground/30',
        'bg-cat-cyan text-cat-cyan-foreground border-cat-cyan-foreground/30',
        'bg-cat-indigo text-cat-indigo-foreground border-cat-indigo-foreground/30',
        'bg-cat-pink text-cat-pink-foreground border-cat-pink-foreground/30',
        'bg-cat-teal text-cat-teal-foreground border-cat-teal-foreground/30',
    ];

    const predefinedIcons = ['↓', '→', '↑', '★', '●', '◆', '▲', '▼', '◄', '►'];

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-2">
                <div className="text-muted-foreground text-xs">
                    {items.length > 0 ? `Current items: ${items.length}` : 'No items configured yet.'}
                </div>
                <ResponsiveModal open={createModalOpen} onOpenChange={setCreateModalOpen} variant="sheet">
                    <ResponsiveModalTrigger asChild>
                        <Button type="button" size="sm" variant="outline">
                            <Plus className="mr-2 h-4 w-4" />
                            Add
                        </Button>
                    </ResponsiveModalTrigger>
                    <ResponsiveModalContent size="lg">
                        <ResponsiveModalHeader>
                            <ResponsiveModalTitle>Add item</ResponsiveModalTitle>
                            <ResponsiveModalDescription>Creates a new option for this setting.</ResponsiveModalDescription>
                        </ResponsiveModalHeader>

                        <ResponsiveModalBody className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label htmlFor="new-value">Value</Label>
                                    <Input
                                        id="new-value"
                                        value={newItem.value}
                                        onChange={(e) => setNewItem({ ...newItem, value: e.target.value })}
                                        placeholder="e.g., local"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="new-label">Label</Label>
                                    <Input
                                        id="new-label"
                                        value={newItem.label}
                                        onChange={(e) => setNewItem({ ...newItem, label: e.target.value })}
                                        placeholder="e.g., Local"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label htmlFor="new-color">Color</Label>
                                    <select
                                        id="new-color"
                                        value={newItem.color}
                                        onChange={(e) => setNewItem({ ...newItem, color: e.target.value })}
                                        className="border-input focus-visible:ring-ring/50 w-full rounded-md border bg-transparent px-3 py-2 text-sm outline-none focus-visible:ring-[3px]"
                                    >
                                        {predefinedColors.map((color) => (
                                            <option key={color} value={color}>
                                                {color.split(' ')[0].replace('bg-', '')}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="new-icon">Icon</Label>
                                    <select
                                        id="new-icon"
                                        value={newItem.icon}
                                        onChange={(e) => setNewItem({ ...newItem, icon: e.target.value })}
                                        className="border-input focus-visible:ring-ring/50 w-full rounded-md border bg-transparent px-3 py-2 text-sm outline-none focus-visible:ring-[3px]"
                                    >
                                        <option value="">No icon</option>
                                        {predefinedIcons.map((icon) => (
                                            <option key={icon} value={icon}>
                                                {icon}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {requiresStage && (
                                <div className="space-y-2">
                                    <Label htmlFor="new-stage">Stage</Label>
                                    <select
                                        id="new-stage"
                                        value={newItem.stage ?? ''}
                                        onChange={(e) => setNewItem({ ...newItem, stage: e.target.value })}
                                        className="border-input focus-visible:ring-ring/50 w-full rounded-md border bg-transparent px-3 py-2 text-sm outline-none focus-visible:ring-[3px]"
                                    >
                                        {stages?.map((stage) => (
                                            <option key={stage} value={stage}>
                                                {labelForStage(stage)}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="text-muted-foreground text-xs">
                                        Determines how the workflow treats this status — which queue it appears in and which date it stamps.
                                    </p>
                                </div>
                            )}

                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="new-terminal"
                                    checked={newItem.isTerminal}
                                    onChange={(e) => setNewItem({ ...newItem, isTerminal: e.target.checked })}
                                    className="rounded"
                                />
                                <Label htmlFor="new-terminal">Terminal status</Label>
                            </div>
                        </ResponsiveModalBody>

                        <ResponsiveModalFooter>
                            <Button type="button" variant="outline" size="sm" onClick={() => setCreateModalOpen(false)}>
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                onClick={handleAddItem}
                                disabled={!newItem.value.trim() || !newItem.label.trim() || (requiresStage && !newItem.stage)}
                            >
                                Add item
                            </Button>
                        </ResponsiveModalFooter>
                    </ResponsiveModalContent>
                </ResponsiveModal>
            </div>

            {/* Existing items */}
            {items.length > 0 && (
                <div className="space-y-2">
                    <Label className="text-muted-foreground text-xs">Drag to reorder</Label>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={items.map((_, index) => index.toString())} strategy={verticalListSortingStrategy}>
                            <div className="space-y-1">
                                {items.map((item, index) => (
                                    <SortableItem
                                        key={index}
                                        id={index.toString()}
                                        item={item}
                                        index={index}
                                        editingIndex={editingIndex}
                                        onEdit={handleEditItem}
                                        onRemove={handleRemoveItem}
                                        onUpdate={handleUpdateItem}
                                        onCancelEdit={handleCancelEdit}
                                        predefinedColors={predefinedColors}
                                        predefinedIcons={predefinedIcons}
                                        stages={stages}
                                        stageLabel={labelForStage}
                                    />
                                ))}
                            </div>
                        </SortableContext>
                    </DndContext>
                </div>
            )}

            {items.length === 0 && (
                <div className="text-muted-foreground py-4 text-center text-sm">No items configured. Add items using the form above.</div>
            )}
        </div>
    );
}

interface SortableItemProps {
    id: string;
    item: StatusItem;
    index: number;
    editingIndex: number | null;
    onEdit: (index: number) => void;
    onRemove: (index: number) => void;
    onUpdate: (index: number, item: StatusItem) => void;
    onCancelEdit: () => void;
    predefinedColors: string[];
    predefinedIcons: string[];
    stages?: readonly string[];
    stageLabel: (stage: string) => string;
}

function SortableItem({
    id,
    item,
    index,
    editingIndex,
    onEdit,
    onRemove,
    onUpdate,
    onCancelEdit,
    predefinedColors,
    predefinedIcons,
    stages,
    stageLabel,
}: SortableItemProps) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div ref={setNodeRef} style={style}>
            <div className="bg-background hover:bg-muted/40 rounded-md border px-2.5 py-2 transition-colors">
                {editingIndex === index ? (
                    <EditItemForm
                        item={item}
                        onSave={(updatedItem) => onUpdate(index, updatedItem)}
                        onCancel={onCancelEdit}
                        predefinedColors={predefinedColors}
                        predefinedIcons={predefinedIcons}
                        stages={stages}
                        stageLabel={stageLabel}
                    />
                ) : (
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex min-w-0 flex-1 items-center gap-2">
                            <button
                                {...attributes}
                                {...listeners}
                                className="text-muted-foreground hover:text-foreground cursor-grab touch-none p-0.5 transition-colors active:cursor-grabbing"
                                aria-label="Drag to reorder"
                            >
                                <GripVertical className="h-4 w-4" />
                            </button>
                            <Badge className={item.color}>
                                {item.icon && <span className="mr-1">{item.icon}</span>}
                                {item.label}
                            </Badge>
                            <span className="text-muted-foreground min-w-0 truncate text-xs">{item.value}</span>
                            {stages?.length && item.stage ? (
                                <Badge variant="outline" className="text-xs">
                                    {stageLabel(item.stage)}
                                </Badge>
                            ) : null}
                            {item.isTerminal ? (
                                <Badge variant="outline" className="text-xs">
                                    Terminal
                                </Badge>
                            ) : null}
                        </div>
                        <div className="flex shrink-0 gap-2">
                            <Button variant="outline" size="sm" onClick={() => onEdit(index)}>
                                Edit
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => onRemove(index)}>
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

interface EditItemFormProps {
    item: StatusItem;
    onSave: (item: StatusItem) => void;
    onCancel: () => void;
    predefinedColors: string[];
    predefinedIcons: string[];
    stages?: readonly string[];
    stageLabel: (stage: string) => string;
}

function EditItemForm({ item, onSave, onCancel, predefinedColors, predefinedIcons, stages, stageLabel }: EditItemFormProps) {
    const [editedItem, setEditedItem] = useState<StatusItem>(item);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label htmlFor="edit-value">Value</Label>
                    <Input id="edit-value" value={editedItem.value} onChange={(e) => setEditedItem({ ...editedItem, value: e.target.value })} />
                </div>
                <div>
                    <Label htmlFor="edit-label">Label</Label>
                    <Input id="edit-label" value={editedItem.label} onChange={(e) => setEditedItem({ ...editedItem, label: e.target.value })} />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label htmlFor="edit-color">Color</Label>
                    <select
                        id="edit-color"
                        value={editedItem.color}
                        onChange={(e) => setEditedItem({ ...editedItem, color: e.target.value })}
                        className="border-input focus:ring-ring w-full rounded-md border px-3 py-2 focus:ring-2 focus:outline-none"
                    >
                        {predefinedColors.map((color) => (
                            <option key={color} value={color}>
                                {color.split(' ')[0].replace('bg-', '')}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <Label htmlFor="edit-icon">Icon</Label>
                    <select
                        id="edit-icon"
                        value={editedItem.icon}
                        onChange={(e) => setEditedItem({ ...editedItem, icon: e.target.value })}
                        className="border-input focus:ring-ring w-full rounded-md border px-3 py-2 focus:ring-2 focus:outline-none"
                    >
                        <option value="">No icon</option>
                        {predefinedIcons.map((icon) => (
                            <option key={icon} value={icon}>
                                {icon}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {stages?.length ? (
                <div>
                    <Label htmlFor="edit-stage">Stage</Label>
                    <select
                        id="edit-stage"
                        value={editedItem.stage ?? ''}
                        onChange={(e) => setEditedItem({ ...editedItem, stage: e.target.value })}
                        className="border-input focus:ring-ring w-full rounded-md border px-3 py-2 focus:ring-2 focus:outline-none"
                    >
                        {stages.map((stage) => (
                            <option key={stage} value={stage}>
                                {stageLabel(stage)}
                            </option>
                        ))}
                    </select>
                </div>
            ) : null}

            <div className="flex items-center space-x-2">
                <input
                    type="checkbox"
                    id="edit-terminal"
                    checked={editedItem.isTerminal}
                    onChange={(e) => setEditedItem({ ...editedItem, isTerminal: e.target.checked })}
                    className="rounded"
                />
                <Label htmlFor="edit-terminal">Terminal status</Label>
            </div>

            <div className="flex gap-2">
                <Button onClick={() => onSave(editedItem)} size="sm">
                    <Save className="mr-2 h-4 w-4" />
                    Save
                </Button>
                <Button variant="outline" onClick={onCancel} size="sm">
                    Cancel
                </Button>
            </div>
        </div>
    );
}
